/**
 * Automatic structured data for pages that don't pass their own.
 *
 * - Breadcrumbs are derived from the URL path (/research/grants →
 *   Home › Research › Grants) so every page is eligible for the
 *   breadcrumb trail in Google results.
 * - The WebPage subtype is picked from the path (AboutPage, ContactPage,
 *   CollectionPage, FAQPage-free) so search engines understand the page role.
 * - Key university pages get an extra entity (campuses, research, admissions…)
 *   linked back to the site-wide CollegeOrUniversity node.
 */
const SITE_URL = "https://www.zetech.ac.ke";
const ORG = { "@id": `${SITE_URL}/#organization` };

/**
 * Organization schema for Zetech University.
 * This provides sitewide structured data for the university entity.
 */
export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "CollegeOrUniversity",
    "@id": `${SITE_URL}/#organization`,
    name: "Zetech University",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/images/logo.png`,
      width: 400,
      height: 400,
    },
    description: "Zetech University is a leading private university in Kenya offering world-class education in technology, business, and innovation.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Off Thika Road, Ruiru",
      addressLocality: "Ruiru",
      addressRegion: "Kiambu",
      addressCountry: "KE",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+254 719 034 500",
      contactType: "admissions",
      areaServed: "KE",
      availableLanguage: "en",
    },
    sameAs: [
      "https://www.facebook.com/ZetechUniversity",
      "https://twitter.com/ZetechUniversity",
      "https://www.linkedin.com/school/zetech-university",
      "https://www.instagram.com/zetechuniversity",
    ],
    areaServed: {
      "@type": "Country",
      name: "Kenya",
    },
    foundedDate: "1999",
    numberOfEmployees: {
      "@type": "QuantitativeValue",
      unitText: "people",
    },
  };
}

/**
 * WebSite schema with search action for voice search.
 * This enables "Hey Google, search Zetech University for..."
 */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Zetech University",
    description: "Zetech University official website - programmes, admissions, research, and campus life.",
    publisher: ORG,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": {
        "@type": "PropertyValueSpecification",
        valueRequired: true,
        valueName: "search_term_string",
      },
    },
  };
}

const LABELS = {
  "about-zu": "About ZU",
  faqs: "FAQs",
  kuccps: "KUCCPS",
  ias: "Institute of Ageing Studies",
  "our-campuses": "Our Campuses",
  "agritech-innovation-hub": "AgriTech Innovation Hub",
  "whistle-blowing": "Whistle Blowing",
  cpc: "CPC",
};

const titleCase = (seg) =>
  LABELS[seg] ||
  decodeURIComponent(seg)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export function autoBreadcrumbs(pathname) {
  const segs = String(pathname || "/")
    .split(/[?#]/)[0]
    .split("/")
    .filter(Boolean);
  if (segs.length === 0) return null;
  const crumbs = [{ name: "Home", path: "/" }];
  let acc = "";
  segs.forEach((seg) => {
    acc += `/${seg}`;
    crumbs.push({ name: titleCase(seg), path: acc });
  });
  return crumbs;
}

const TYPE_RULES = [
  [/^\/(about-zu|mission-vision|our-foundation|charter|governance|zetech-anthem)$/, "AboutPage"],
  [/^\/(contact|contact-us|student-support)$/, "ContactPage"],
  [/^\/(programmes|blog|events|research|institutes|testimonials)(\/(school|level|qualification)\/[^/]+)?$/, "CollectionPage"],
];

export function autoWebPageType(pathname) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  const hit = TYPE_RULES.find(([rx]) => rx.test(p));
  return hit ? hit[1] : "WebPage";
}

const CAMPUSES = [
  { name: "Zetech University — Ruiru Campus", street: "Off Thika Road, Ruiru", locality: "Ruiru" },
  { name: "Zetech University — Mang'u Campus", street: "Thika–Mang'u Road", locality: "Thika" },
];

export function autoPageEntities(pathname, canonical) {
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  switch (p) {
    case "/our-campuses":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${canonical}#campuses`,
          name: "Zetech University campuses",
          itemListElement: CAMPUSES.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Place",
              name: c.name,
              containedInPlace: ORG,
              address: {
                "@type": "PostalAddress",
                streetAddress: c.street,
                addressLocality: c.locality,
                addressCountry: "KE",
              },
            },
          })),
        },
      ];
    case "/research":
    case "/research/projects":
    case "/research/patents":
    case "/research/consultancies":
    case "/research/grants":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ResearchProject",
          "@id": `${canonical}#research`,
          name: `Zetech University ${p === "/research" ? "Research & Innovation" : titleCase(p.split("/").pop())}`,
          url: canonical,
          parentOrganization: ORG,
        },
      ];
    case "/admissions":
    case "/application-process":
    case "/intakes":
      return [
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": `${canonical}#howto`,
          name: "How to apply to Zetech University",
          step: [
            { "@type": "HowToStep", position: 1, name: "Choose a programme", url: `${SITE_URL}/programmes` },
            { "@type": "HowToStep", position: 2, name: "Check the fees", url: `${SITE_URL}/tuition-fees` },
            { "@type": "HowToStep", position: 3, name: "Submit your application", url: `${SITE_URL}/application-process` },
          ],
        },
      ];
    default:
      return [];
  }
}
